import { datadogLogs } from '@datadog/browser-logs';
import { RumInitConfiguration, datadogRum } from '@datadog/browser-rum';
import { Session } from '@ory/client/api';
import { signal } from '@preact/signals-react';
import { ConsentStatusEnum } from '@pypestream/api-services';
import { Page, PageBody, Spinner } from '@pypestream/design-system';
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import React, { useEffect } from 'react';

import { removeTrailingSlash } from '@pypestream/utils';
import { kratosApi } from './kratos.api';
import {
  sendGlobalAppEvent,
  sendUserEvent,
  useUserCtxSelector,
} from './xstate/app.xstate';

export type WindowProduktly = Window &
  typeof globalThis & {
    Produktly: {
      identifyUser: (
        userId?: string,
        metadata?: Record<string, unknown>
      ) => void;
    };
  };

const session = signal<Session | null>(null);

kratosApi
  .toSession()
  .then(({ data }) => {
    session.value = data;

    sendGlobalAppEvent({
      type: 'user.signIn',
      user: {
        // I'm trying to avoid passing in this info, only to have it immediately get overwritten
        // by our getCurrentUser GQL query
        //
        // email: data.identity.traits.email,
        // firstName: data.identity.traits.firstName,
        // lastName: data.identity.traits.lastName,
        // profilePhoto: data.identity.traits.picture,
        kratosId: data.identity.id,
      },
    });
  })
  .catch((error) => {
    console.error('Session error:', JSON.stringify(error));
    sendUserEvent('user.signOut');
  });

export const AuthProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}): JSX.Element => {
  const posthog = usePostHog();

  const { pathname } = window.location;

  const pathAsArray = removeTrailingSlash(pathname).split('/').filter(Boolean);
  const isHomePage = pathname === '/' || pathAsArray.length === 2;

  const { optionalConsentStatus, userId, userEmail, userName } =
    useUserCtxSelector((ctx) => ({
      optionalConsentStatus: ctx.user?.optionalConsentStatus,
      userId: ctx.user?.id,
      userEmail: ctx.user?.email,
      userName: `${ctx.user?.firstName} ${ctx.user?.lastName}`,
    }));

  useEffect(() => {
    if (!optionalConsentStatus || !posthog) {
      if (!optionalConsentStatus) {
        console.log('>>>>>>> PostHog: Waiting for consent status');
      }
      if (!posthog) {
        console.log('>>>>>>> PostHog: PostHog instance not available');
      }
      return;
    }

    // Log current status before making changes
    const statusBefore = {
      hasOptedIn: posthog.has_opted_in_capturing(),
      sessionRecordingStarted: posthog.sessionRecordingStarted(),
    };
    console.log('>>>>>>> PostHog: Consent status changed', {
      consentStatus: optionalConsentStatus,
      statusBefore,
    });

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      console.log('>>>>>>> PostHog: Opting in and starting session recording');

      // Check if PostHog is fully loaded before proceeding
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isLoaded = (posthog as any).__loaded;
      let sessionRecordingConfig: unknown = 'unknown';
      try {
        if (typeof posthog.get_config === 'function') {
          sessionRecordingConfig =
            posthog.get_config('session_recording') || 'unknown';
        }
      } catch (e) {
        // get_config not available
      }
      console.log('>>>>>>> PostHog: Before opt-in - PostHog loaded status', {
        isLoaded,
        hasOptedIn: posthog.has_opted_in_capturing(),
        sessionRecordingConfig,
      });

      posthog.opt_in_capturing();

      // Log before starting recording
      console.log('>>>>>>> PostHog: About to start session recording', {
        hasOptedIn: posthog.has_opted_in_capturing(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        currentStatus: (posthog.sessionRecording as any)?.status,
      });

      posthog.startSessionRecording();

      if (userId) {
        console.log('>>>>>>> PostHog: Identifying user', {
          userId,
          userEmail,
          userName,
        });
        posthog.identify(userId, {
          email: userEmail,
          name: userName,
          app: 'pro-studio',
        });
      }
    } else {
      console.log('>>>>>>> PostHog: Opting out and stopping session recording');
      posthog.stopSessionRecording();
      posthog.opt_out_capturing();
    }
  }, [optionalConsentStatus, posthog, userEmail, userId, userName]);

  useEffect(() => {
    if (!optionalConsentStatus) return;

    let datadogTrackingConsent: RumInitConfiguration['trackingConsent'] =
      'not-granted';

    if (optionalConsentStatus === ConsentStatusEnum.Accepted) {
      datadogTrackingConsent = 'granted';
      datadogRum.startSessionReplayRecording();
      (window as WindowProduktly).Produktly.identifyUser(userId, {
        email: userEmail,
        name: userName,
      });
      console.log('start capturing');
    } else {
      datadogRum.stopSessionReplayRecording();
      Sentry.close().then(function () {
        console.log('opting out of Sentry');
      });
    }

    // https://docs.datadoghq.com/logs/log_collection/javascript/#user-tracking-consent
    datadogLogs.setTrackingConsent(datadogTrackingConsent);
    datadogRum.setTrackingConsent(datadogTrackingConsent);
  }, [optionalConsentStatus, userEmail, userId, userName]);

  // TODO remove if not needed (should not show white page without it)
  if (!session.value) {
    return (
      <Page>
        <PageBody background="none">
          <Spinner fullpage />
        </PageBody>
      </Page>
    );
  }

  // todo: do we need this?
  // const splittedPath = window.location.href.split('?');
  // window.history.pushState('authenticated', '', splittedPath[0]);
  return <>{children}</>;
};
